// CreateResiliencyRunModal - Modal to create a new resiliency test run
import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Database, Shield, Info, Terminal, Play } from 'lucide-react';
import { useSessionJwt } from '../../../hooks/useSessionJwt';

const CreateResiliencyRunModal = ({ appName, appId, onClose, onCreate }) => {
  const { sessionId, getJwt } = useSessionJwt();
  const [name, setName] = useState('');
  const [mode, setMode] = useState('cli'); // 'cli' or 'interactive'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [selectedTestIds, setSelectedTestIds] = useState(new Set());

  const [datasets, setDatasets] = useState([]);
  const [datasetsLoading, setDatasetsLoading] = useState(true);
  const [datasetsError, setDatasetsError] = useState(null);

  const [tests, setTests] = useState([]);
  const [testsLoading, setTestsLoading] = useState(true);
  const [testsError, setTestsError] = useState(null);

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Fetch available datasets for this app/service
  useEffect(() => {
    const fetchDatasets = async () => {
      const jwt = getJwt();
      if (!jwt || !appId) return;

      setDatasetsLoading(true);
      setDatasetsError(null);

      try {
        const response = await fetch(
          `${apiUrl}/evals/apps/${appId}/datasets`,
          {
            headers: {
              'Authorization': `Bearer ${jwt}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch datasets: ${response.status}`);
        }

        const data = await response.json();
        setDatasets(data.datasets || []);
      } catch (err) {
        console.error('Failed to fetch datasets:', err);
        setDatasetsError(err.message);
      } finally {
        setDatasetsLoading(false);
      }
    };

    fetchDatasets();
  }, [sessionId, getJwt, apiUrl, appId]);

  // Fetch test catalog (filtered by app's span kinds, but NOT by category)
  useEffect(() => {
    const fetchTests = async () => {
      const jwt = getJwt();
      if (!jwt || !appId) return;

      setTestsLoading(true);
      setTestsError(null);

      try {
        // Build query params - include service_id to filter by app's span kinds
        // Don't filter by component here - we want all tests to build category list
        const queryParams = new URLSearchParams();
        queryParams.set('service_id', appId);
        const response = await fetch(
          `${apiUrl}/resiliency/tests?${queryParams.toString()}`,
          {
            headers: {
              'Authorization': `Bearer ${jwt}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch tests: ${response.status}`);
        }

        const data = await response.json();
        setTests(data.tests || []);
      } catch (err) {
        console.error('Failed to fetch tests:', err);
        setTestsError(err.message);
      } finally {
        setTestsLoading(false);
      }
    };

    fetchTests();
  }, [sessionId, getJwt, apiUrl, appId]); // Removed selectedCategory dependency

  // Count tests by category and build dynamic category list
  const testsByCategory = tests.reduce((acc, test) => {
    acc[test.component] = (acc[test.component] || 0) + 1;
    return acc;
  }, {});

  // Build categories dynamically from tests - "All" first, then sorted by count
  const categories = useMemo(() => {
    const cats = [{ id: 'all', name: 'All Tests' }];
    const sortedComponents = Object.entries(testsByCategory)
      .sort((a, b) => b[1] - a[1]) // Sort by count descending
      .map(([component]) => ({ id: component, name: component }));
    return [...cats, ...sortedComponents];
  }, [testsByCategory]);

  // Filter tests by selected category
  const filteredTests = useMemo(() => {
    if (selectedCategory === 'all') return tests;
    return tests.filter(t => t.component === selectedCategory);
  }, [tests, selectedCategory]);

  // Toggle individual test selection
  const toggleTest = (testId) => {
    setSelectedTestIds(prev => {
      const next = new Set(prev);
      if (next.has(testId)) {
        next.delete(testId);
      } else {
        next.add(testId);
      }
      return next;
    });
  };

  // Select/deselect all tests in current category
  const toggleAllTests = () => {
    if (selectedTestIds.size === filteredTests.length) {
      // Deselect all
      setSelectedTestIds(new Set());
    } else {
      // Select all in current category
      setSelectedTestIds(new Set(filteredTests.map(t => t.test_id)));
    }
  };

  // Clear selection when category changes
  useEffect(() => {
    setSelectedTestIds(new Set());
  }, [selectedCategory]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    // Dataset is only required for CLI mode
    if (mode === 'cli' && !selectedDatasetId) {
      setError('Please select a dataset for CLI mode');
      return;
    }

    setCreating(true);
    setError(null);

    try {
      const runConfig = {
        name: name.trim(),
        dataset_id: mode === 'cli' ? selectedDatasetId : '', // Empty for interactive mode
        category: selectedCategory
      };

      // If specific tests are selected, include test_ids
      if (selectedTestIds.size > 0) {
        runConfig.test_ids = Array.from(selectedTestIds);
      }

      await onCreate(runConfig);
    } catch (err) {
      setError(err.message);
      setCreating(false);
    }
  };

  // Count of tests to run (selected tests or all in category)
  const testsToRunCount = selectedTestIds.size > 0
    ? selectedTestIds.size
    : filteredTests.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-background-secondary border border-border-subtle rounded-lg w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 pb-4 flex-shrink-0 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-accent-primary-base" />
            <h3 className="text-lg font-semibold text-text-primary">Create Resiliency Run</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-text-muted hover:text-text-primary transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 pt-4">
          {/* Run Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-1">
              Run Name <span className="text-status-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary-base"
              placeholder="e.g., Full Resiliency Suite - v1.0"
              autoFocus
            />
          </div>

          {/* Run Mode Toggle */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Run Mode
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('cli')}
                className={`flex items-center gap-2 text-left px-3 py-2 rounded border transition-colors ${
                  mode === 'cli'
                    ? 'border-accent-primary-base bg-accent-primary-base/10 text-accent-primary-base'
                    : 'border-border-subtle hover:border-accent-primary-base/50 text-text-primary'
                }`}
              >
                <Terminal size={16} />
                <div>
                  <div className="text-sm font-medium">CLI Harness</div>
                  <div className="text-xs text-text-muted">Automated with dataset</div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setMode('interactive')}
                className={`flex items-center gap-2 text-left px-3 py-2 rounded border transition-colors ${
                  mode === 'interactive'
                    ? 'border-accent-primary-base bg-accent-primary-base/10 text-accent-primary-base'
                    : 'border-border-subtle hover:border-accent-primary-base/50 text-text-primary'
                }`}
              >
                <Play size={16} />
                <div>
                  <div className="text-sm font-medium">Interactive</div>
                  <div className="text-xs text-text-muted">Manual inputs in app</div>
                </div>
              </button>
            </div>
          </div>

          {/* Dataset Selection - Only for CLI mode */}
          {mode === 'cli' && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-text-primary mb-1">
                <Database size={14} className="inline mr-1" />
                Dataset <span className="text-status-danger">*</span>
              </label>
              {datasetsLoading ? (
                <div className="flex items-center gap-2 px-3 py-2 text-text-muted">
                  <Loader2 size={14} className="animate-spin" />
                  Loading datasets...
                </div>
              ) : datasetsError ? (
                <div className="text-sm text-status-danger">{datasetsError}</div>
              ) : datasets.length === 0 ? (
                <div className="text-sm text-text-muted">
                  No datasets found. Create a dataset in the Evaluate section first.
                </div>
              ) : (
                <select
                  value={selectedDatasetId}
                  onChange={(e) => setSelectedDatasetId(e.target.value)}
                  className="w-full px-3 py-2 bg-background-tertiary border border-border-subtle rounded text-text-primary focus:outline-none focus:border-accent-primary-base"
                >
                  <option value="">Select a dataset...</option>
                  {datasets.map(dataset => (
                    <option key={dataset.id} value={dataset.id}>
                      {dataset.name} ({dataset.example_count || 0} examples)
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-text-muted">
                Each dataset example will be run against the selected tests
              </p>
            </div>
          )}

          {/* Interactive mode info */}
          {mode === 'interactive' && (
            <div className="mb-4 p-3 bg-accent-primary-base/5 border border-accent-primary-base/20 rounded">
              <p className="text-sm text-text-primary">
                Interactive mode lets you run your app normally while injecting faults.
                After creating the run, set these environment variables:
              </p>
              <code className="block mt-2 p-2 bg-background-tertiary rounded text-xs text-accent-secondary-base font-mono">
                LUMOZ_RESILIENCY_MODE=1<br />
                LUMOZ_RESILIENCY_RUN_ID=&lt;run_id&gt;
              </code>
            </div>
          )}

          {/* Test Category */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-primary mb-2">
              Test Category
            </label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`text-left px-3 py-2 rounded border transition-colors ${
                    selectedCategory === cat.id
                      ? 'border-accent-primary-base bg-accent-primary-base/10 text-accent-primary-base'
                      : 'border-border-subtle hover:border-accent-primary-base/50 text-text-primary'
                  }`}
                >
                  <div className="text-sm font-medium">{cat.name}</div>
                  <div className="text-xs text-text-muted">
                    {cat.id === 'all' ? `${tests.length} tests` : `${testsByCategory[cat.id] || 0} tests`}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Test Selection */}
          <div className="mb-6 p-3 bg-background-tertiary rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <Info size={14} />
                <span>
                  Tests to run: {testsLoading ? '...' : testsToRunCount}
                  {selectedTestIds.size > 0 && (
                    <span className="ml-1 text-accent-primary-base">
                      ({selectedTestIds.size} selected)
                    </span>
                  )}
                </span>
              </div>
              {!testsLoading && filteredTests.length > 0 && (
                <button
                  type="button"
                  onClick={toggleAllTests}
                  className="text-xs text-accent-primary-base hover:underline"
                >
                  {selectedTestIds.size === filteredTests.length ? 'Deselect all' : 'Select all'}
                </button>
              )}
            </div>
            {!testsLoading && filteredTests.length > 0 && (
              <div className="max-h-48 overflow-y-auto space-y-1">
                {filteredTests.map(t => (
                  <label
                    key={t.test_id}
                    className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                      selectedTestIds.has(t.test_id)
                        ? 'bg-accent-primary-base/10 border border-accent-primary-base/30'
                        : 'hover:bg-background-secondary border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTestIds.has(t.test_id)}
                      onChange={() => toggleTest(t.test_id)}
                      className="mt-0.5 accent-accent-warm-base"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-text-primary truncate">{t.scenario}</div>
                      <div className="text-xs text-text-muted truncate">{t.failure_mode}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {selectedTestIds.size === 0 && !testsLoading && (
              <p className="text-xs text-text-muted mt-2">
                All {filteredTests.length} tests in this category will run. Select specific tests to run fewer.
              </p>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-status-danger/10 border border-status-danger/30 rounded text-sm text-status-danger">
              {error}
            </div>
          )}
          </div>

          {/* Actions - Fixed Footer */}
          <div className="flex justify-end gap-3 p-6 pt-4 flex-shrink-0 border-t border-border-subtle">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !name.trim() || (mode === 'cli' && !selectedDatasetId)}
              className="flex items-center gap-2 px-4 py-2 bg-accent-primary-base text-text-inverse rounded font-medium text-sm hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Shield size={14} />
                  Create Run
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateResiliencyRunModal;
